import { useRef } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import { Camera, Printer, Ruler, Aperture, ArrowsOutCardinal } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { usePDFExport } from "@/utils/pdfExport";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
const itemAnim = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

// ---------------------------------------------------------------------------
// Diagram: concentric sensor outlines on a single full-frame "scene".
// Demonstrates how smaller sensors crop into the full-frame image.
// ---------------------------------------------------------------------------
function SensorComparison() {
  // Full-frame = 36 x 24 mm = scale baseline.
  // We draw all sensors centered, each smaller than the last.
  const sensors = [
    { name: "Medium Format", w: 44, h: 33, color: "#A78BFA", crop: 0.79 },
    { name: "Full Frame (35mm)", w: 36, h: 24, color: "#F5A524", crop: 1.0 },
    { name: "APS-C (Canon)", w: 22.3, h: 14.9, color: "#22C55E", crop: 1.6 },
    { name: "APS-C (Nikon/Sony/Fuji)", w: 23.5, h: 15.6, color: "#3B82F6", crop: 1.5 },
    { name: "Micro 4/3", w: 17.3, h: 13, color: "#EF4444", crop: 2.0 },
    { name: "1-inch", w: 13.2, h: 8.8, color: "#EC4899", crop: 2.7 },
  ];
  const W = 480;
  const DIAGRAM_H = 260;
  const LEGEND_H = 130;
  const H = DIAGRAM_H + LEGEND_H;
  const scale = Math.min(W / 50, DIAGRAM_H / 38);
  const cx = W / 2;
  const cy = DIAGRAM_H / 2;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block">
      <rect x="0" y="0" width={W} height={H} fill="#0A0A0A" />
      {/* Grid (diagram area only) */}
      {[0.25, 0.5, 0.75].map((p, i) => (
        <line key={`v-${i}`} x1={W * p} y1="0" x2={W * p} y2={DIAGRAM_H} stroke="#1F1F23" strokeWidth="0.5" />
      ))}
      {[0.25, 0.5, 0.75].map((p, i) => (
        <line key={`h-${i}`} x1="0" y1={DIAGRAM_H * p} x2={W} y2={DIAGRAM_H * p} stroke="#1F1F23" strokeWidth="0.5" />
      ))}
      {sensors.map((s, i) => {
        const w = s.w * scale;
        const h = s.h * scale;
        return (
          <rect
            key={s.name}
            x={cx - w / 2}
            y={cy - h / 2}
            width={w}
            height={h}
            fill="none"
            stroke={s.color}
            strokeWidth="2"
            strokeDasharray={i === 1 ? "" : "4 2"}
            opacity={0.95}
          />
        );
      })}
      {/* Divider between diagram and legend */}
      <line x1="20" y1={DIAGRAM_H + 6} x2={W - 20} y2={DIAGRAM_H + 6} stroke="#1F1F23" strokeWidth="0.5" />

      {/* Legend — 2 columns x 3 rows, color swatch + name + crop factor */}
      {sensors.map((s, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const colW = (W - 40) / 2;
        const startX = 20 + col * colW;
        const startY = DIAGRAM_H + 28 + row * 32;
        return (
          <g key={`legend-${s.name}`}>
            <rect
              x={startX}
              y={startY - 9}
              width={14}
              height={10}
              fill="none"
              stroke={s.color}
              strokeWidth="2"
              strokeDasharray={i === 1 ? "" : "3 1.5"}
            />
            <text
              x={startX + 20}
              y={startY}
              fontSize="10"
              fill={s.color}
              fontFamily="monospace"
              fontWeight="600"
            >
              {s.name}
            </text>
            <text
              x={startX + 20}
              y={startY + 12}
              fontSize="9"
              fill="#A1A1AA"
              fontFamily="monospace"
            >
              {`${s.crop}x crop`}
            </text>
          </g>
        );
      })}

      <text x={W / 2} y={H - 8} fontSize="9" fill="#71717A" textAnchor="middle" fontFamily="monospace">
        All sensors drawn to relative scale • Centered • Full-frame = 1.0x baseline
      </text>
    </svg>
  );
}

const CONTENT = {
  en: {
    page_title: "Focal Length & Crop Factor: Sensor Size Explained",
    page_sub:
      "A 50mm lens does not look the same on every camera. A smaller sensor 'crops' the image, making your lens behave like a longer one. Understand crop factor and you will never buy the wrong lens for your body again.",
    download: "Download as PDF",
    generating: "Generating...",

    intro_title: "What sensor size is",
    intro_body:
      "Inside every digital camera, behind the lens, is a rectangular image sensor that captures light. Sensors come in standard sizes — and the size determines what portion of the lens's image circle is recorded. A bigger sensor sees more of the scene; a smaller sensor sees only the middle. The original 35mm film frame (36 x 24 mm) is the historic baseline — anything that size is called 'full-frame'. Anything smaller has a 'crop factor' relative to it.",
    intro_body_2:
      "Crop factor is just a multiplier. Multiply your lens's real focal length by the crop factor to find the 'equivalent full-frame focal length' — the angle of view your photo will appear to have when compared to a 35mm film standard.",

    diagram_title: "The 6 standard sensor sizes — to scale",
    diagram_caption: "Larger rectangle = bigger sensor = wider field of view at the same focal length.",

    table_title: "Crop factors and effective focal lengths",
    table_intro: "Here is what a 50mm lens 'becomes' on each common sensor size:",
    table_cols: ["Sensor", "Common Bodies", "Crop Factor", "50mm lens =", "200mm lens ="],
    table_rows: [
      ["Medium Format", "Fuji GFX, Hasselblad", "0.79x", "~40mm", "~158mm"],
      ["Full Frame (35mm)", "Canon R5/R6, Nikon Z6/Z8, Sony A7/A1", "1.0x (baseline)", "50mm", "200mm"],
      ["APS-C (Canon)", "Canon R7, R10, M50, 90D", "1.6x", "80mm", "320mm"],
      ["APS-C (Nikon DX, Sony APS-C, Fuji X)", "Nikon Z50, Sony A6700, Fuji X-T5", "1.5x", "75mm", "300mm"],
      ["Micro Four Thirds", "Olympus / OM-System, Panasonic Lumix G", "2.0x", "100mm", "400mm"],
      ["1-inch", "Sony RX100, Canon G7X, premium compacts", "2.7x", "135mm", "540mm"],
    ],

    math_title: "The crop-factor math (it is easy)",
    math_body:
      "Effective focal length = lens focal length × crop factor. Examples below. Note: the lens DOES NOT physically change. Only the angle of view changes because the smaller sensor captures less of the image circle.",
    math_examples: [
      "A 35mm lens on a Canon APS-C (R10) → 35 × 1.6 = 56mm equivalent — perfect 'normal' field of view",
      "A 50mm lens on a Nikon APS-C (Z50) → 50 × 1.5 = 75mm equivalent — flattering for portraits",
      "A 70-200mm telephoto on a Micro 4/3 (OM-1) → 140-400mm equivalent — affordable wildlife reach",
      "A 16mm wide-angle on an APS-C body → only 24mm equivalent — losing wide-angle drama",
    ],

    pros_cons_title: "Trade-offs of smaller sensors",
    pros: [
      "Cheaper bodies and cheaper lenses (especially for telephoto reach)",
      "Smaller / lighter — easier to carry all day",
      "Crop factor = free 'zoom' for wildlife, sports, birding",
      "Deeper depth of field at the same aperture (more in focus)",
    ],
    cons: [
      "Wide-angle work is harder — a 24mm lens on APS-C is only 36mm equivalent",
      "Less low-light performance — smaller pixels collect less light, higher ISO is noisier",
      "Shallower DOF (blurred background) is harder to achieve — you need wider apertures",
      "Smaller viewfinder image on optical (DSLR) bodies",
    ],

    lens_designations_title: "Lens designations — which lens fits which body",
    lens_intro:
      "Manufacturers mark lenses by which sensor size they cover. Putting a full-frame lens on an APS-C body works fine (the body just uses the center of the lens image circle). But putting an APS-C-only lens on a full-frame body causes heavy vignetting because the lens's image circle is too small.",
    lens_rows: [
      ["Canon", "RF (full-frame), RF-S (APS-C only). Older: EF (FF), EF-S (APS-C only)"],
      ["Nikon", "Z FX (full-frame), Z DX (APS-C only). Older F-mount: full-frame default, DX = APS-C only"],
      ["Sony", "FE (full-frame), E (APS-C only). FE lenses work on E bodies; E lenses on FE cause vignetting"],
      ["Fujifilm", "XF / XC (APS-C X-mount), GF (Medium Format GFX-mount). Different mounts — not interchangeable"],
      ["Micro 4/3", "Any 'Micro 4/3' branded lens fits any 'Micro 4/3' body (Olympus/OM, Panasonic) — shared mount"],
    ],

    dof_title: "Depth-of-field implications",
    dof_body:
      "Sensor size also affects depth of field. To get the SAME subject framing, you have to stand at the same distance — but at the same aperture, a smaller-sensor body has more depth of field (more in focus from front to back). Practical example: an 85mm f/1.8 lens on a full-frame body produces creamy background blur. The same 85mm f/1.8 on an APS-C body still produces blur, but less of it, AND your framing is now tighter (a 130mm equivalent). To match the full-frame look you need an APS-C 56mm f/1.2 — which exists, but costs more.",

    which_to_buy_title: "Which sensor for which photographer",
    which_to_buy: [
      "Sports / Wildlife / Birding: APS-C or Micro 4/3 — the crop-factor 'reach bonus' beats expensive long telephotos",
      "Landscape / Architecture / Real Estate: Full-frame — wide-angle remains wide, high dynamic range matters",
      "Portrait / Weddings / Events: Full-frame for shallow-DOF aesthetic, APS-C high-end for budget conscious",
      "Street / Travel: APS-C — compact, light, capable, and small lenses",
      "Studio / Commercial: Full-frame or Medium Format — color, dynamic range, large print sizes",
      "Vlogging / Hybrid Photo+Video: Full-frame for cinematic look, APS-C for cost and reach",
    ],

    pro_tip:
      "Pro tip: do not chase 'full-frame because the magazines say so.' Modern APS-C bodies (Fuji X-T5, Sony A6700, Canon R7) produce gorgeous professional images and save you thousands on lenses. Decide first what you shoot, then pick the sensor — not the other way around.",
  },

  es: {
    page_title: "Distancia Focal y Factor de Recorte: Tamaño de Sensor Explicado",
    page_sub:
      "Un lente de 50mm no se ve igual en toda camara. Un sensor mas pequeño 'recorta' la imagen, haciendo que tu lente actue como uno mas largo. Entiende el factor de recorte y nunca mas compraras el lente equivocado para tu cuerpo.",
    download: "Descargar como PDF",
    generating: "Generando...",

    intro_title: "Que es el tamaño de sensor",
    intro_body:
      "Dentro de cada camara digital, detras del lente, hay un sensor rectangular que captura la luz. Los sensores vienen en tamaños estandar — y el tamaño determine que porcion del circulo de imagen del lente se registra. Un sensor mas grande ve mas de la escena; uno mas pequeño solo ve el centro. El cuadro original de pelicula de 35mm (36 x 24 mm) es la base historica — cualquier cosa de ese tamaño se llama 'full-frame' (cuadro completo). Cualquier sensor mas pequeño tiene un 'factor de recorte' relativo a este.",
    intro_body_2:
      "El factor de recorte es solo un multiplicador. Multiplica la distancia focal real de tu lente por el factor de recorte para hallar la 'distancia focal equivalente en full-frame' — el angulo de vision que parecera tener tu foto comparado con el estandar de pelicula de 35mm.",

    diagram_title: "Los 6 tamaños de sensor estandar — a escala",
    diagram_caption: "Rectangulo mas grande = sensor mas grande = mayor angulo de vision a la misma distancia focal.",

    table_title: "Factores de recorte y distancias focales efectivas",
    table_intro: "Esto es en lo que se 'convierte' un lente de 50mm en cada tamaño de sensor comun:",
    table_cols: ["Sensor", "Cuerpos Comunes", "Factor de Recorte", "50mm =", "200mm ="],
    table_rows: [
      ["Medium Format", "Fuji GFX, Hasselblad", "0.79x", "~40mm", "~158mm"],
      ["Full Frame (35mm)", "Canon R5/R6, Nikon Z6/Z8, Sony A7/A1", "1.0x (base)", "50mm", "200mm"],
      ["APS-C (Canon)", "Canon R7, R10, M50, 90D", "1.6x", "80mm", "320mm"],
      ["APS-C (Nikon DX, Sony APS-C, Fuji X)", "Nikon Z50, Sony A6700, Fuji X-T5", "1.5x", "75mm", "300mm"],
      ["Micro Four Thirds", "Olympus / OM-System, Panasonic Lumix G", "2.0x", "100mm", "400mm"],
      ["1-inch", "Sony RX100, Canon G7X, compactas premium", "2.7x", "135mm", "540mm"],
    ],

    math_title: "La matematica del factor de recorte (es facil)",
    math_body:
      "Distancia focal efectiva = distancia focal del lente × factor de recorte. Ejemplos abajo. Nota: el lente NO cambia fisicamente. Solo cambia el angulo de vision porque el sensor mas pequeño captura menos del circulo de imagen.",
    math_examples: [
      "Un lente de 35mm en una Canon APS-C (R10) → 35 × 1.6 = 56mm equivalente — un campo de vision 'normal' perfecto",
      "Un lente de 50mm en una Nikon APS-C (Z50) → 50 × 1.5 = 75mm equivalente — favorecedor para retratos",
      "Un telefoto 70-200mm en Micro 4/3 (OM-1) → 140-400mm equivalente — alcance asequible para vida silvestre",
      "Un gran angular de 16mm en cuerpo APS-C → solo 24mm equivalente — pierde el drama del gran angular",
    ],

    pros_cons_title: "Compromisos de los sensores mas pequeños",
    pros: [
      "Cuerpos y lentes mas baratos (especialmente para alcance telefoto)",
      "Mas pequeños / livianos — mas faciles de cargar todo el dia",
      "El factor de recorte = 'zoom' gratis para vida silvestre, deportes, aves",
      "Mayor profundidad de campo a la misma apertura (mas en foco)",
    ],
    cons: [
      "El trabajo gran angular es mas dificil — un lente 24mm en APS-C es solo 36mm equivalente",
      "Menor rendimiento en poca luz — pixeles mas pequeños recogen menos luz, ISO alto ruidoso",
      "Profundidad de campo reducida (fondo desenfocado) mas dificil de lograr — necesitas aperturas mas amplias",
      "Imagen mas pequeña en visores opticos (DSLR)",
    ],

    lens_designations_title: "Denominaciones de lentes — cual lente cabe en cual cuerpo",
    lens_intro:
      "Los fabricantes marcan los lentes segun que tamaño de sensor cubren. Poner un lente full-frame en un cuerpo APS-C funciona bien (el cuerpo solo usa el centro del circulo de imagen del lente). Pero poner un lente solo-APS-C en un cuerpo full-frame causa fuerte vinieteado porque el circulo de imagen del lente es demasiado pequeño.",
    lens_rows: [
      ["Canon", "RF (full-frame), RF-S (solo APS-C). Antiguos: EF (FF), EF-S (solo APS-C)"],
      ["Nikon", "Z FX (full-frame), Z DX (solo APS-C). Montura F antigua: full-frame por defecto, DX = solo APS-C"],
      ["Sony", "FE (full-frame), E (solo APS-C). Los lentes FE funcionan en cuerpos E; lentes E en FE causan vinieteado"],
      ["Fujifilm", "XF / XC (APS-C montura X), GF (Medium Format montura GFX). Monturas distintas — no intercambiables"],
      ["Micro 4/3", "Cualquier lente 'Micro 4/3' encaja en cualquier cuerpo 'Micro 4/3' (Olympus/OM, Panasonic) — montura compartida"],
    ],

    dof_title: "Implicaciones en la profundidad de campo",
    dof_body:
      "El tamaño de sensor tambien afecta la profundidad de campo. Para obtener el MISMO encuadre del sujeto debes pararte a la misma distancia — pero a la misma apertura, un cuerpo con sensor mas pequeño tiene mas profundidad de campo (mas en foco de adelante hacia atras). Ejemplo practico: un lente 85mm f/1.8 en full-frame produce un fondo cremoso desenfocado. El mismo 85mm f/1.8 en APS-C aun produce desenfoque, pero menos, Y tu encuadre ahora es mas cerrado (130mm equivalente). Para igualar el look full-frame en APS-C necesitas un 56mm f/1.2 — que existe, pero cuesta mas.",

    which_to_buy_title: "Que sensor para que fotografo",
    which_to_buy: [
      "Deportes / Vida silvestre / Aves: APS-C o Micro 4/3 — el 'bonus de alcance' del recorte le gana a los caros telefotos largos",
      "Paisaje / Arquitectura / Bienes raices: Full-frame — el gran angular sigue siendo amplio, el rango dinamico importa",
      "Retrato / Bodas / Eventos: Full-frame para la estetica de PDC reducida, APS-C de gama alta para presupuestos ajustados",
      "Calle / Viajes: APS-C — compacto, liviano, capaz, y lentes pequeños",
      "Estudio / Comercial: Full-frame o Medium Format — color, rango dinamico, tamaños grandes de impresion",
      "Vlogging / Foto+Video hibrido: Full-frame para look cinematografico, APS-C para costo y alcance",
    ],

    pro_tip:
      "Tip pro: no persigas 'full-frame porque las revistas lo dicen'. Los cuerpos APS-C modernos (Fuji X-T5, Sony A6700, Canon R7) producen imagenes profesionales hermosas y te ahorran miles en lentes. Decide primero que fotografias, luego elige el sensor — no al reves.",
  },
};

export default function CropFactorPage() {
  const { lang } = useTranslation();
  const c = CONTENT[lang] || CONTENT.en;
  const printRef = useRef(null);
  const { ready, generating, download } = usePDFExport(
    printRef,
    () => `crop-factor-${lang}.pdf`,
    [lang]
  );

  return (
    <div
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:bg-white print:text-black"
      data-testid="cropfactor-page"
    >
      <div className="print:hidden">
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-8">
          <motion.div variants={itemAnim} className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2">
                {c.page_title}
              </h1>
              <p className="text-[#71717A] text-base max-w-3xl">{c.page_sub}</p>
            </div>
            <Button
              onClick={download}
              disabled={!ready}
              className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60"
              data-testid="cropfactor-download-btn"
            >
              <Printer className="w-4 h-4 mr-1.5" />
              {generating ? c.generating : c.download}
            </Button>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Camera weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.intro_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.intro_body}</p>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{c.intro_body_2}</p>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <ArrowsOutCardinal weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.diagram_title}</h2>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 mb-3">
              <SensorComparison />
            </div>
            <p className="text-[#71717A] text-xs italic text-center">{c.diagram_caption}</p>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Ruler weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.table_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm mb-3">{c.table_intro}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[#F5A524] border-b border-white/10">
                    {c.table_cols.map((col, i) => (
                      <th key={i} className="text-left py-2 px-2 font-medium uppercase tracking-widest text-[10px]">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {c.table_rows.map((row, i) => (
                    <tr key={i} className="border-b border-white/5">
                      {row.map((cell, j) => (
                        <td
                          key={j}
                          className={`py-2 px-2 ${j === 0 ? "text-white font-medium" : j === 2 ? "text-[#F5A524] font-['JetBrains_Mono']" : "text-[#A1A1AA]"}`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-3">{c.math_title}</h2>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.math_body}</p>
            <ul className="space-y-2">
              {c.math_examples.map((ex, i) => (
                <li key={i} className="flex items-start gap-2 bg-[#0A0A0A] border border-white/10 rounded p-3">
                  <span className="text-[#F5A524] font-['JetBrains_Mono'] text-xs mt-0.5">{`>`}</span>
                  <span className="text-[#A1A1AA] text-xs leading-relaxed font-['JetBrains_Mono']">{ex}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-3">{c.pros_cons_title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-emerald-950/30 border border-emerald-500/30 rounded-lg p-4">
                <p className="text-emerald-400 font-medium text-sm mb-2">+ Pros</p>
                <ul className="space-y-1.5">
                  {c.pros.map((p, i) => (
                    <li key={i} className="text-[#D4D4D8] text-xs leading-relaxed flex gap-2">
                      <span className="text-emerald-400">•</span>{p}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-rose-950/30 border border-rose-500/30 rounded-lg p-4">
                <p className="text-rose-400 font-medium text-sm mb-2">− Cons</p>
                <ul className="space-y-1.5">
                  {c.cons.map((p, i) => (
                    <li key={i} className="text-[#D4D4D8] text-xs leading-relaxed flex gap-2">
                      <span className="text-rose-400">•</span>{p}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Aperture weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.lens_designations_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.lens_intro}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {c.lens_rows.map((row, i) => (
                <div
                  key={i}
                  className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3"
                  data-testid={`cropfactor-lens-${row[0].toLowerCase().replace(/[^a-z0-9]/g, "")}`}
                >
                  <p className="text-[#F5A524] font-medium text-sm mb-1.5">{row[0]}</p>
                  <p className="text-[#A1A1AA] text-xs leading-relaxed">{row[1]}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-3">{c.dof_title}</h2>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{c.dof_body}</p>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-3">{c.which_to_buy_title}</h2>
            <ul className="space-y-2">
              {c.which_to_buy.map((w, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[#F5A524] shrink-0" />
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{w}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#F5A524]/10 border border-[#F5A524]/30 rounded-xl p-4">
            <p className="text-[#F5A524] text-sm leading-relaxed italic">{c.pro_tip}</p>
          </motion.div>
        </motion.div>
      </div>

      {/* Print */}
      <div ref={printRef} className="hidden print:block bg-white text-black p-4" data-testid="cropfactor-print-view">
        <h1 className="text-3xl font-bold text-center mb-1">{c.page_title}</h1>
        <p className="text-center text-gray-500 mb-5 text-sm">{c.page_sub}</p>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">1. {c.intro_title}</h2>
        <p className="text-sm text-gray-700 mb-2">{c.intro_body}</p>
        <p className="text-sm text-gray-700 mb-5">{c.intro_body_2}</p>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">2. {c.table_title}</h2>
        <p className="text-sm text-gray-700 mb-2">{c.table_intro}</p>
        <table className="w-full text-xs border-collapse mb-4">
          <thead>
            <tr className="bg-gray-100">{c.table_cols.map((col, i) => (<th key={i} className="border border-gray-300 p-2 text-left">{col}</th>))}</tr>
          </thead>
          <tbody>
            {c.table_rows.map((row, i) => (
              <tr key={i}>{row.map((cell, j) => (<td key={j} className="border border-gray-300 p-2">{cell}</td>))}</tr>
            ))}
          </tbody>
        </table>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">3. {c.math_title}</h2>
        <p className="text-sm text-gray-700 mb-2">{c.math_body}</p>
        <ul className="list-disc ml-5 space-y-1 mb-4">{c.math_examples.map((ex, i) => (<li key={i} className="text-xs text-gray-700 font-mono">{ex}</li>))}</ul>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">4. {c.pros_cons_title}</h2>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div><p className="font-bold text-emerald-700 text-sm">+ Pros</p><ul className="list-disc ml-5 space-y-1">{c.pros.map((p, i) => (<li key={i} className="text-xs text-gray-700">{p}</li>))}</ul></div>
          <div><p className="font-bold text-rose-700 text-sm">− Cons</p><ul className="list-disc ml-5 space-y-1">{c.cons.map((p, i) => (<li key={i} className="text-xs text-gray-700">{p}</li>))}</ul></div>
        </div>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">5. {c.lens_designations_title}</h2>
        <p className="text-sm text-gray-700 mb-2">{c.lens_intro}</p>
        <table className="w-full text-xs border-collapse mb-4">
          <tbody>{c.lens_rows.map((row, i) => (<tr key={i}><td className="border border-gray-300 p-2 font-semibold w-1/5">{row[0]}</td><td className="border border-gray-300 p-2">{row[1]}</td></tr>))}</tbody>
        </table>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">6. {c.dof_title}</h2>
        <p className="text-sm text-gray-700 mb-3">{c.dof_body}</p>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">7. {c.which_to_buy_title}</h2>
        <ul className="list-disc ml-5 space-y-1 mb-3">{c.which_to_buy.map((w, i) => (<li key={i} className="text-xs text-gray-700">{w}</li>))}</ul>
        <p className="text-xs text-gray-700 italic"><strong>Pro tip:</strong> {c.pro_tip}</p>
        <p className="text-center text-xs text-gray-400 mt-4">www.dslradvisor.com</p>
      </div>
    </div>
  );
}
